import { useState, useEffect, useRef, FC, FormEvent } from 'react';
import { CloseOutlined, SendOutlined, InfoCircleOutlined, CommentOutlined, StopOutlined, ArrowsAltOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import {
  setIsOpen,
  addMessage,
  setIsConnected,
  setIsLoading,
  setIsInputDisabled,
  Message,
  chatSelector,
  moduleMessagesSelector,
  ChatContext,
  setResult
} from '../../../store/slices/chatSlice';
import ReactMarkdown from 'react-markdown';

interface ChatContextConfig {
  id: string;
  title: string;
  wsUrl: string;
  allowClose: boolean;
}

interface MethodOutput {
  [key: string]: any;
}

interface WebSocketMessage {
  type: string;
  step?: string;
  message?: string;
  method_output?: string;
  status?: string;
  action?: string;
  result?: any;
}

const AiAgentChat: FC = () => {
  const dispatch = useDispatch();
  const {
    isOpen,
    isConnected,
    isLoading,
    isInputDisabled,
    currentContext,
    contextData,
    reInit
  } = useSelector(chatSelector);
  const messages = useSelector(moduleMessagesSelector(currentContext || 'default'));

  const [userInput, setUserInput] = useState('');
  const [currentStep, setCurrentStep] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const socketRef = useRef<WebSocket | null>(null);

  // Resize state
  const [dimensions, setDimensions] = useState({ width: 350, height: 500 });
  const isResizing = useRef(false);
  const startPos = useRef({ x: 0, y: 0, w: 0, h: 0 });

  const startResizing = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    isResizing.current = true;
    startPos.current = {
      x: e.clientX,
      y: e.clientY,
      w: dimensions.width,
      h: dimensions.height
    };
    document.addEventListener('mousemove', handleResize);
    document.addEventListener('mouseup', stopResizing);
    document.body.style.cursor = 'nwse-resize';
  };

  const handleResize = (e: MouseEvent) => {
    if (!isResizing.current) return;

    const deltaX = startPos.current.x - e.clientX;
    const deltaY = startPos.current.y - e.clientY;

    const newWidth = Math.max(300, Math.min(window.innerWidth - 40, startPos.current.w + deltaX));
    const newHeight = Math.max(400, Math.min(window.innerHeight - 40, startPos.current.h + deltaY));

    setDimensions({ width: newWidth, height: newHeight });
  };

  const stopResizing = () => {
    isResizing.current = false;
    document.removeEventListener('mousemove', handleResize);
    document.removeEventListener('mouseup', stopResizing);
    document.body.style.cursor = 'default';
  };

  const contextConfigs: ChatContextConfig[] = [
    {
      id: ChatContext.LITERATURE_SEARCH,
      title: "Literature Search Assistant",
      wsUrl: "wss://rag.coguide.in/literature/ws",
      allowClose: true
    },
    {
      id: ChatContext.RELEVANCE_CHECK,
      title: "Relevance Check Assistant",
      wsUrl: "wss://rag.coguide.in/evaluate",
      allowClose: true
    }
  ];

  const currentConfig = currentContext ? contextConfigs.find(item => item.id === currentContext) : null;
  const chatTitle = currentConfig?.title || "AI Assistant";
  const allowClose = currentConfig?.allowClose || true;

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (currentContext && contextData) {
      initializeWebSocket();
    }

    return () => {
      closeWebSocket();
    };
  }, [currentContext, contextData, reInit]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const initializeWebSocket = () => {
    if (!currentContext || !contextData || !currentConfig) {
      console.error("Missing context data for WebSocket connection");
      addSystemMessage("Error: Missing configuration for chat connection.");
      return;
    }

    try {
      // Close existing WebSocket if it exists
      closeWebSocket();

      const token = localStorage.getItem('coguide_token');
      if (!token) {
        console.error("Authentication token is missing");
        addSystemMessage("Error: Authentication token is missing. Please login again.");
        return;
      }

      // Build WebSocket URL based on the context
      let wsUrl = currentConfig.wsUrl;
      if (currentContext === ChatContext.LITERATURE_SEARCH && contextData.request_id) {
        wsUrl = `${wsUrl}/${contextData.request_id}`;
      }
      else if (currentContext === ChatContext.RELEVANCE_CHECK && contextData.topicId && contextData.query) {
        wsUrl = `${wsUrl}?topic_id=${encodeURIComponent(contextData.topicId)}&query=${encodeURIComponent(contextData.query)}&token=${encodeURIComponent(token)}`;
      }
      else {
        console.error("Invalid context data for WebSocket URL construction");
        addSystemMessage("Error: Missing required parameters for connection.");
        return;
      }

      // Create WebSocket connection
      socketRef.current = new WebSocket(wsUrl);

      // Set loading state
      dispatch(setIsLoading(true));
      dispatch(setIsInputDisabled(true));

      // Connection opened
      socketRef.current.onopen = () => {
        console.log("WebSocket connection established");
        dispatch(setIsConnected(true));
        addSystemMessage("Connected to server");

        // Add initial message based on context
        if (currentContext === ChatContext.LITERATURE_SEARCH) {
          // addAiMessage("I'm initializing the literature search based on your topic. Please wait a moment...");
        } else if (currentContext === ChatContext.RELEVANCE_CHECK) {
          addAiMessage(`Starting relevance check`);
        }
      };

      // Listen for messages
      socketRef.current.onmessage = (event) => {
        handleWebSocketMessage(event.data);
      };

      // Handle WebSocket errors
      socketRef.current.onerror = (error) => {
        console.error("WebSocket error:", error);
        dispatch(setIsLoading(false));
        dispatch(setIsConnected(false));
        dispatch(setIsInputDisabled(false));
        addSystemMessage("There was an error connecting to the service. Please try again later.");
      };

      // Handle WebSocket close
      socketRef.current.onclose = () => {
        console.log("WebSocket connection closed");
        dispatch(setIsLoading(false));
        dispatch(setIsConnected(false));
        addSystemMessage("Disconnected from server");
      };

    } catch (error) {
      console.error("Error establishing WebSocket connection:", error);
      dispatch(setIsLoading(false));
      dispatch(setIsConnected(false));
      dispatch(setIsInputDisabled(false));
      addSystemMessage("Failed to establish connection");
    }
  };

  const handleWebSocketMessage = (data: string) => {
    try {
      const msg: WebSocketMessage = JSON.parse(data);
      const msgType = msg.type;

      switch (msgType) {
        case 'FEEDBACK_REQUEST':
          handleFeedbackRequest(msg);
          break;

        case 'STATUS':
          handleStatusMessage(msg);
          break;

        case 'COMPLETED':
          handleCompletedMessage(msg);
          break;

        case 'FEEDBACK_ACK':
          handleFeedbackAck(msg);
          break;

        case 'ERROR':
          handleErrorMessage(msg);
          break;

        default:
          // Handle legacy message format for backward compatibility
          handleLegacyMessage(msg);
          break;
      }
    } catch (error) {
      console.error("Error parsing WebSocket message:", error);
      addSystemMessage("Error processing message from server");
    }
  };

  const handleFeedbackRequest = (msg: WebSocketMessage) => {
    const step = msg.step || 'unknown';
    const message = msg.message || '';
    const methodOutput = msg.method_output;

    setCurrentStep(step);
    dispatch(setIsLoading(false));
    dispatch(setIsInputDisabled(false));

    // Add AI message with optional method output
    addAiMessage(message, methodOutput);

    if (step === "post_evaluation_refinement" && currentContext === ChatContext.LITERATURE_SEARCH) {
      dispatch(setResult({
        module: currentContext || 'default',
        resultStatus: 'success'
      }));
    }
  };

  const handleStatusMessage = (msg: WebSocketMessage) => {
    const status = msg.status || '';
    const message = msg.message || '';

    // Show all status messages, not just specific ones
    addSystemMessage(`${status}: ${message}`);

    if (status === 'Failed') {
      dispatch(setIsLoading(false));
      dispatch(setIsInputDisabled(true));
      dispatch(setResult({
        module: currentContext || 'default',
        resultStatus: 'Failed'
      }));
    } else {
      // Keep loading state active for all other status messages
      dispatch(setIsLoading(true));
    }
  };

  const handleCompletedMessage = (msg: WebSocketMessage) => {
    dispatch(setIsLoading(false));
    dispatch(setIsInputDisabled(true));
    addSystemMessage("🎉 Flow Completed!");

    if (currentContext === ChatContext.LITERATURE_SEARCH) {
      addAiMessage("Search complete! I've found relevant articles based on your criteria. You can now review them by clicking on view.");
    } else if (currentContext === ChatContext.RELEVANCE_CHECK) {
      const relevanceScore = msg.result?.relevance_score;
      const relevanceAnalysis = msg.result?.analysis;

      let resultMessage = "Relevance check complete!";
      if (relevanceScore !== undefined) {
        resultMessage += ` Score: ${relevanceScore}`;
      }

      addAiMessage(resultMessage);
    }

    dispatch(setResult({
      module: currentContext || 'default',
      resultStatus: 'success'
    }));

    closeWebSocket();
  };

  const handleFeedbackAck = (msg: WebSocketMessage) => {
    const action = msg.action || 'received';
    addSystemMessage(`✓ ${action}`);
  };

  const handleErrorMessage = (msg: WebSocketMessage) => {
    const errorText = msg.message || 'An error occurred. Please Re-Initiate the process.';
    dispatch(setIsLoading(false));
    addSystemMessage(`Error: ${errorText}`);
    closeWebSocket();
  };

  // Handle legacy message format for backward compatibility
  const handleLegacyMessage = (msg: any) => {
    const eventKey = Object.keys(msg).find((key) => key === "status" || key === "assistant");

    if (eventKey === "status") {
      // Always show status message
      addSystemMessage(`${msg.status}: ${msg.step || ''}`);

      if (msg.status === "Completed") {
        handleCompletedMessage({
          type: 'COMPLETED',
          result: msg.result
        });
      } else if (msg.status === "Failed") {
        dispatch(setIsLoading(false));
        dispatch(setIsInputDisabled(true));
        dispatch(setResult({
          module: currentContext || 'default',
          resultStatus: 'Failed'
        }));
      } else {
        dispatch(setIsLoading(true));
      }
    }
    else if (eventKey === "assistant") {
      dispatch(setIsLoading(false));
      addAiMessage(msg.assistant);
      dispatch(setIsInputDisabled(false));
    }
  };

  const closeWebSocket = () => {
    if (socketRef.current) {
      socketRef.current.close();
      socketRef.current = null;
      dispatch(setIsConnected(false));
      setCurrentStep(null);
    }
  };

  const addAiMessage = (text: string, methodOutput?: string) => {
    dispatch(addMessage({
      module: currentContext || 'default',
      message: {
        sender: 'ai',
        text,
        timestamp: new Date().toISOString(),
        methodOutput
      }
    }));
  };

  const addSystemMessage = (text: string) => {
    dispatch(addMessage({
      module: currentContext || 'default',
      message: {
        sender: 'system',
        text,
        timestamp: new Date().toISOString()
      }
    }));
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (userInput.trim() && !isInputDisabled && socketRef.current) {
      // Add user message to chat
      dispatch(addMessage({
        module: currentContext || 'default',
        message: {
          sender: 'user',
          text: userInput,
          timestamp: new Date().toISOString()
        }
      }));

      // Send feedback to WebSocket
      if (socketRef.current.readyState === WebSocket.OPEN) {
        const payload = {
          type: 'feedback',
          step: currentStep || 'unknown',
          content: userInput
        };

        socketRef.current.send(JSON.stringify(payload));
        dispatch(setIsLoading(true));
        dispatch(setIsInputDisabled(true));
      } else {
        addSystemMessage("The connection to the service has been lost. Please refresh the page and try again.");
      }

      setUserInput('');

      // Reset textarea height
      if (inputRef.current) {
        inputRef.current.style.height = 'auto';
      }
    }
  };

  // Auto-resize textarea height based on content
  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setUserInput(e.target.value);

    // Reset height to get the correct scrollHeight
    e.target.style.height = 'auto';
    // Set height to scrollHeight + small buffer
    e.target.style.height = `${e.target.scrollHeight}px`;
  };

  // Handle Enter key for submission (with Shift+Enter for new line)
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e as unknown as FormEvent);
    }
  };

  const handleClose = () => {
    dispatch(setIsOpen(false));
  };

  const handleOpen = () => {
    dispatch(setIsOpen(true));
  };

  // Helper function to format timestamp
  const formatTime = (timestamp: string): string => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Render method output if present
  const renderMethodOutput = (methodOutput?: string) => {
    if (!methodOutput) return null;

    return (
      <div className="method-output-container">
        <div className="method-output-header">
          📋 Method Output
        </div>
        <div className="method-output-content">
          {/* <pre>{JSON.stringify(methodOutput, null, 2)}</pre> */}
          <ReactMarkdown>{methodOutput}</ReactMarkdown>
        </div>
      </div>
    );
  };

  return (
    <>
      <div
        className={`ai-agent-chat ${isOpen ? 'open' : ''}`}
        style={{ width: `${dimensions.width}px`, height: `${dimensions.height}px` }}
      >
        <div className="resize-handle-custom" onMouseDown={startResizing} title="Drag to resize">
          <ArrowsAltOutlined />
        </div>
        <div className="ai-agent-chat-header">
          <div className="chat-title">
            <InfoCircleOutlined />
            <span>{chatTitle}</span>
          </div>
          <div className="header-right">
            <div className={`connection-indicator ${isConnected ? 'connected' : 'disconnected'}`}
              title={isConnected ? 'Connected' : 'Disconnected'}>
            </div>
            {isConnected && (
              <div className='stop-button' onClick={closeWebSocket} title="Stop connection">
                <StopOutlined />
              </div>
            )}
            {allowClose && (
              <button className="close-button" onClick={handleClose}>
                <CloseOutlined />
              </button>
            )}
          </div>
        </div>

        <div className="ai-agent-chat-messages" ref={chatContainerRef}>
          {messages.map((message: any, index: number) => (
            <div key={index}>
              {message.sender === 'system' ? (
                <div className="system-message">
                  <span>{message.text}</span>
                </div>
              ) : (
                <div
                  className={`message-container ${message.sender === 'ai' ? 'ai-container' : 'user-container'}`}
                >
                  <div className="avatar">
                    {message.sender === 'ai' ? 'AI' : 'You'}
                  </div>
                  <div className={`message ${message.sender === 'ai' ? 'ai-message' : 'user-message'}`}>
                    <div className="message-content">
                      {message.text}
                    </div>
                    {message.timestamp && (
                      <div className="message-timestamp">
                        {formatTime(message.timestamp)}
                      </div>
                    )}
                  </div>
                </div>
              )}
              {message.methodOutput && renderMethodOutput(message.methodOutput)}
            </div>
          ))}
          {isLoading && (
            <div className="message-container ai-container">
              <div className="avatar">AI</div>
              <div className="message ai-message">
                <div className="message-content typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <form className="ai-agent-chat-input" onSubmit={handleSubmit}>
          <div className="textarea-container">
            <textarea
              ref={inputRef}
              value={userInput}
              onChange={handleTextareaChange}
              onKeyDown={handleKeyDown}
              placeholder={isInputDisabled ? "Waiting for response..." : "Message AI Assistant..."}
              disabled={isInputDisabled}
              rows={1}
            />
            <button
              type="submit"
              disabled={!userInput.trim() || isInputDisabled}
              className={!userInput.trim() || isInputDisabled ? 'disabled' : ''}
            >
              <SendOutlined />
            </button>
          </div>
          <div className="input-footer">
            <span className="input-tip">Press Enter to send, Shift+Enter for new line</span>
          </div>
        </form>
      </div>

      {!isOpen && (
        <button className="ai-agent-chat-button" onClick={handleOpen}>
          <CommentOutlined />
        </button>
      )}
    </>
  );
};

export default AiAgentChat;